import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Mail,
  Eye,
  MousePointerClick,
  CheckCircle2,
  RefreshCcw,
  ArrowLeft,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lightweight, admin-only funnel dashboard.
 *
 * Renders: delivered → opened → clicked → check_in_completed
 * plus three derived rates (open / click / click-through).
 *
 * Polls the summary endpoint on mount and on a manual refresh button only —
 * no auto-refresh, keeping it cheap and predictable.
 */
const STAT_CARDS = [
  {
    key: 'delivered',
    label: 'Delivered',
    sub: 'Emails accepted by Resend',
    icon: Mail,
    accent: 'bg-blue-50 text-blue-700 border-blue-100',
    dot: 'bg-blue-500',
  },
  {
    key: 'opened',
    label: 'Opened',
    sub: 'Tracking pixel fetched',
    icon: Eye,
    accent: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    dot: 'bg-emerald-500',
  },
  {
    key: 'clicked',
    label: 'Clicked',
    sub: 'CTA redirect followed',
    icon: MousePointerClick,
    accent: 'bg-purple-50 text-purple-700 border-purple-100',
    dot: 'bg-purple-500',
  },
  {
    key: 'check_in_completed',
    label: 'Check-ins',
    sub: 'First daily submit',
    icon: CheckCircle2,
    accent: 'bg-amber-50 text-amber-800 border-amber-100',
    dot: 'bg-amber-500',
  },
];

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const RateRow = ({ label, value, total }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-b-0">
    <div>
      <p className="text-sm font-medium text-gray-900">{label}</p>
      <p className="text-xs text-gray-500 mt-0.5">{total}</p>
    </div>
    <p className="text-base font-semibold tabular-nums text-gray-900">
      {value.toFixed(1)}%
    </p>
  </div>
);

const EmailAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/email-events/summary`, {
        withCredentials: true,
      });
      setData(res.data);
    } catch (err) {
      const detail =
        err?.response?.status === 403
          ? "You're not signed in as the admin. Sign in with the admin account and try again."
          : err?.response?.data?.detail || 'Failed to load analytics.';
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = data?.counts || {};
  const rates = data?.rates || {};

  return (
    <div
      className="min-h-screen bg-gray-50 pb-24"
      data-testid="email-analytics-page"
    >
      <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-8 sm:pt-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 mb-3"
              data-testid="email-analytics-back"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to dashboard
            </Link>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Email funnel
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Delivered → Opened → Clicked → Check-in completed
            </p>
          </div>
          <Button
            onClick={load}
            disabled={loading}
            variant="outline"
            size="sm"
            className="gap-2"
            data-testid="email-analytics-refresh"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCcw className="h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>

        {/* Error */}
        {error && (
          <div
            className="mb-6 border border-red-100 bg-red-50 rounded-xl p-4 flex items-start gap-3"
            data-testid="email-analytics-error"
          >
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-900">Couldn't load funnel data.</p>
              <p className="text-sm text-red-800/80 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Loading skeleton */}
        {loading && !data && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {STAT_CARDS.map((card) => (
              <div
                key={card.key}
                className="border border-gray-100 bg-white rounded-2xl p-5 h-32 animate-pulse"
              />
            ))}
          </div>
        )}

        {/* Stat cards */}
        {data && (
          <>
            <div
              className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
              data-testid="email-analytics-stats"
            >
              {STAT_CARDS.map(({ key, label, sub, icon: Icon, accent, dot }) => {
                const value = counts[key] ?? 0;
                return (
                  <div
                    key={key}
                    className="relative border border-gray-100 bg-white rounded-2xl p-5 shadow-sm"
                    data-testid={`email-stat-${key}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent}`}
                      >
                        <Icon className="h-4.5 w-4.5" />
                      </div>
                      <span className={`h-2 w-2 rounded-full ${dot}`} />
                    </div>
                    <p
                      className="text-3xl font-bold text-gray-900 tabular-nums leading-none"
                      data-testid={`email-stat-${key}-value`}
                    >
                      {value.toLocaleString()}
                    </p>
                    <p className="text-sm font-semibold text-gray-900 mt-3">{label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
                  </div>
                );
              })}
            </div>

            {/* Rates + meta */}
            <div className="grid lg:grid-cols-3 gap-4">
              <div
                className="lg:col-span-2 border border-gray-100 bg-white rounded-2xl p-5 shadow-sm"
                data-testid="email-analytics-rates"
              >
                <p className="text-sm font-semibold text-gray-900 mb-3">Funnel rates</p>
                <RateRow
                  label="Open rate"
                  value={rates.open_rate ?? 0}
                  total={`${counts.opened ?? 0} of ${counts.delivered ?? 0} delivered`}
                />
                <RateRow
                  label="Click rate"
                  value={rates.click_rate ?? 0}
                  total={`${counts.clicked ?? 0} of ${counts.delivered ?? 0} delivered`}
                />
                <RateRow
                  label="Click-through rate"
                  value={rates.click_through_rate ?? 0}
                  total={`${counts.clicked ?? 0} of ${counts.opened ?? 0} opened`}
                />
              </div>
              <div className="border border-gray-100 bg-white rounded-2xl p-5 shadow-sm">
                <p className="text-sm font-semibold text-gray-900 mb-3">Last activity</p>
                <p
                  className="text-sm text-gray-700"
                  data-testid="email-analytics-last-event"
                >
                  {formatTimestamp(data.most_recent_event_at)}
                </p>
                <p className="text-xs text-gray-500 mt-3">
                  Pixel-based opens require image-loading in the email client. Some
                  privacy-respecting clients (Apple Mail Privacy Protection) auto-load
                  pixels and may inflate opens.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailAnalytics;
